import React, { useState, useEffect } from 'react';
import { Button, Modal, DatePicker, Space, notification, Alert, Checkbox } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { usePointOfSaleService } from '../../../services/pointofsale/PointOfSaleServiceProvider';
import { useMemberService } from '../../../services/members/MemberServiceProvider';
import { useMember } from '../../../common/member/MemberContext';
import dayjs, { Dayjs } from 'dayjs';
import { MemberProfile } from '../../../model/members/MemberProfile';
import { PointOfSaleTransaction } from '../../../model/pointofsale/PointOfSaleTransaction';
import { PointOfSaleItem } from '../../../model/pointofsale/PointOfSaleItem';
import { MembershipCategory } from '../../../model/members/MembershipCategory';
import MembershipCategorySelector from '../MembershipCategorySelector';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useReceiptPrinterService } from '../../../services/printing/ReceiptPrinterServiceProvider';
import { calculateValidToDate } from '../../../utils/membershipDateUtils';
import { toDateOnly } from '../../../utils/DateOnly';
import { useCustomAmount } from '../../../hooks/useCustomAmount';
import { updateMemberDetails, createTransactionItem, createTransactionRequest, processReceipt } from '../../../utils/membershipTransactionUtils';
import { CustomAmountInput } from './shared/CustomAmountInput';
import { TransactionSummary } from './shared/TransactionSummary';
import { StatusSummary } from './shared/StatusSummary';
import { useFormatCurrency } from '../../../hooks/useFormatters';
import { useClientConfig } from '../../../common/client/ClientConfigProvider';

interface CreateMembershipSubscriptionTransactionProps {
  member: MemberProfile;
  pointOfSaleTransactions: PointOfSaleTransaction[];
  membershipCategories: MembershipCategory[];
  onTransactionUpdated?: () => void;
  onMemberUpdated?: () => void;
  hideButton?: boolean;
  parentModalVisible?: boolean;
  onParentCancel?: () => void;
}

const CreateMembershipSubscriptionTransaction: React.FC<CreateMembershipSubscriptionTransactionProps> = ({
  member,
  pointOfSaleTransactions,
  membershipCategories,
  onTransactionUpdated,
  onMemberUpdated,
  hideButton = false,
  parentModalVisible = false,
  onParentCancel
}) => {
  const [isModalVisible, setIsModalVisible] = useState(!hideButton ? false : parentModalVisible);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(dayjs());
  const [selectedMembershipCategory, setSelectedMembershipCategory] = useState<MembershipCategory | null>(member.membershipCategory);
  const [amount, setAmount] = useState<number | null>(null);
  const [updateValidTo, setUpdateValidTo] = useState(true);
  const [validToDate, setValidToDate] = useState<Dayjs | null>(null);
  const [useCustomValidTo, setUseCustomValidTo] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [membershipSubscriptionPosItem, setMembershipSubscriptionPosItem] = useState<PointOfSaleItem | null>(null);
  const receiptPrinterService = useReceiptPrinterService();
  const pointOfSaleService = usePointOfSaleService();
  const memberService = useMemberService();
  const { isAdmin } = useMember();
  const formatCurrency = useFormatCurrency();
  const { billing } = useClientConfig();

  const customAmountHook = useCustomAmount(amount);

  // Fetch the Membership Subscription POS item
  useEffect(() => {
    const fetchMembershipSubscriptionPosItem = async () => {
      try {
        // Try common names for membership subscription
        const possibleNames = [
          'Membership Subscription',
          
        ];
        
        let item: PointOfSaleItem | null = null;
        for (const name of possibleNames) {
          try {
            item = await pointOfSaleService.getPosItem(name);
            if (item) break;
          } catch (error) {
            // Continue to next name
          }
        }
        
        if (!item) {
          // If not found, try to get all items and find one that might be subscription
          const allItems = await pointOfSaleService.getPosItems();
          item = allItems.find(i => 
            i.name.toLowerCase().includes('subscription') || 
            i.name.toLowerCase().includes('membership')
          ) || null;
        }
        
        setMembershipSubscriptionPosItem(item);
        // Amount will be set based on membership category, not POS item price
      } catch (error) {
        console.error('Error fetching membership subscription POS item:', error);
        notification.warning({
          message: 'Warning',
          description: 'Could not find Membership Subscription POS item. Please ensure it exists in the system.'
        });
      }
    };
    
    if (isModalVisible) {
      fetchMembershipSubscriptionPosItem();
    }
  }, [isModalVisible, pointOfSaleService]);

  // Calculate amount when membership category changes
  useEffect(() => {
    if (selectedMembershipCategory) {
      // For subscription, use the full membership category amount
      const calculatedAmount = selectedMembershipCategory.amount;
      setAmount(calculatedAmount);
      // Reset custom amount when calculated amount changes
      if (!customAmountHook.useCustomAmount) {
        customAmountHook.setCustomAmount(calculatedAmount);
      }
    }
  }, [selectedMembershipCategory, customAmountHook]);

  // Calculate validTo date when month or category changes
  useEffect(() => {
    if (selectedMonth && selectedMembershipCategory && updateValidTo && !useCustomValidTo) {
      const calculatedValidTo = calculateValidToDate(selectedMonth, selectedMembershipCategory, billing);
      setValidToDate(calculatedValidTo);
    }
  }, [selectedMonth, selectedMembershipCategory, updateValidTo, useCustomValidTo, billing]);

  // Check if validTo date is different from member's current validTo
  const isValidToChanged = () => {
    if (!validToDate || !member.validTo) return false;
    return !validToDate.isSame(dayjs(member.validTo), 'day');
  };

  const handleMonthChange = (date: Dayjs | null) => {
    setSelectedMonth(date);
  };

  const handleMembershipCategoryChange = (category: MembershipCategory | null) => {
    setSelectedMembershipCategory(category);
  };

  const handleUseCustomAmountChange = (e: CheckboxChangeEvent) => {
    customAmountHook.handleUseCustomAmountChange(e);
    if (!e.target.checked && selectedMembershipCategory) {
      // Reset to membership category amount when custom amount is disabled
      setAmount(selectedMembershipCategory.amount);
    }
  };

  const handleUpdateValidToChange = (e: CheckboxChangeEvent) => {
    setUpdateValidTo(e.target.checked);
    if (e.target.checked && selectedMonth && selectedMembershipCategory && !useCustomValidTo) {
      const calculatedValidTo = calculateValidToDate(selectedMonth, selectedMembershipCategory, billing);
      setValidToDate(calculatedValidTo);
    }
  };

  const handleCustomValidToChange = (e: CheckboxChangeEvent) => {
    setUseCustomValidTo(e.target.checked);
    if (!e.target.checked && selectedMonth && selectedMembershipCategory && updateValidTo) {
      const calculatedValidTo = calculateValidToDate(selectedMonth, selectedMembershipCategory, billing);
      setValidToDate(calculatedValidTo);
    }
  };

  const handleValidToDateChange = (date: Dayjs | null) => {
    setValidToDate(date);
  };

  const getFinalAmount = () => {
    return customAmountHook.getFinalAmount(amount);
  };

  const processTransaction = async (transactionStatus: string, successMessage: string) => {
    if (!selectedMonth || !membershipSubscriptionPosItem || !selectedMembershipCategory) {
      notification.error({
        message: 'Error',
        description: 'Please select a membership month, category and ensure subscription item is available'
      });
      return;
    }

    const finalAmount = getFinalAmount();
    if (!finalAmount || finalAmount <= 0) {
      notification.error({
        message: 'Error',
        description: 'Please enter a valid amount for the transaction'
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Update member if marked as paid
      if (transactionStatus === 'Completed' && selectedMembershipCategory) {
        const validToStr = updateValidTo && validToDate ? toDateOnly(validToDate) ?? null : null;
        await updateMemberDetails(member, memberService, selectedMembershipCategory, validToStr, onMemberUpdated);
      }

      // Create transaction
      const transactionItem = createTransactionItem(membershipSubscriptionPosItem, finalAmount);
      const createRequest = createTransactionRequest(member.membershipId, [transactionItem], finalAmount, transactionStatus);
      const transaction = await pointOfSaleService.makePayment(createRequest);
      
      await processReceipt(transaction, receiptPrinterService, transactionStatus, member.name);
      
      notification.success({
        message: 'Success',
        description: successMessage
      });
      
      setIsModalVisible(false);
      if (onTransactionUpdated) {
        onTransactionUpdated();
      }
    } catch (error) {
      console.error('Error processing membership subscription transaction:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to process membership subscription transaction'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessTransaction = async () => {
    await processTransaction("Completed", "Membership subscription transaction created successfully");
  };

  const handleMarkAsUnpaid = async () => {
    await processTransaction("Unpaid", "Membership subscription transaction marked as unpaid successfully");
  };

  const showModal = () => {
    setIsModalVisible(true);
    setSelectedMonth(dayjs());
    setSelectedMembershipCategory(member.membershipCategory);
    customAmountHook.reset();
    setUpdateValidTo(true);
    setUseCustomValidTo(false);
    
    // Set initial amount based on member's category
    if (member.membershipCategory) {
      setAmount(member.membershipCategory.amount);
    } else {
      setAmount(null);
    }
    
    // Set initial validTo based on current month and member's category
    const initialValidTo = calculateValidToDate(dayjs(), member.membershipCategory, billing);
    setValidToDate(initialValidTo);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedMonth(dayjs());
    customAmountHook.reset();
    setAmount(null);
    setUpdateValidTo(true);
    setUseCustomValidTo(false);
    setValidToDate(null);
  };

  // Update modal visibility when parentModalVisible changes (for parent control)
  React.useEffect(() => {
    if (hideButton) {
      setIsModalVisible(parentModalVisible);
    }
  }, [parentModalVisible, hideButton]);

  if (!isAdmin) {
    return null;
  }

  const formContent = (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
            message="Membership Subscription Transaction"
            description="Create a transaction for a specific membership month. If marked as paid, the member's validTo date will be updated."
            type="info"
            showIcon
          />

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Membership Category: *</div>
            <MembershipCategorySelector
              membershipCategories={membershipCategories}
              onChange={handleMembershipCategoryChange}
              value={selectedMembershipCategory?.name}
              disabled={isProcessing}
            />
            {!selectedMembershipCategory && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                Please select a membership category to proceed
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Membership Month/Year: *</div>
            <DatePicker
              picker="month"
              format="MMMM YYYY"
              value={selectedMonth}
              onChange={handleMonthChange}
              style={{ width: '100%' }}
              disabled={isProcessing}
              placeholder="Select membership month"
            />
            {!selectedMonth && (
              <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                Please select a membership month to proceed
              </div>
            )}
            {selectedMonth && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Selected: {selectedMonth.format('MMMM YYYY')}
              </div>
            )}
          </div>

          <CustomAmountInput
            useCustomAmount={customAmountHook.useCustomAmount}
            customAmount={customAmountHook.customAmount}
            defaultAmount={amount}
            onUseCustomAmountChange={handleUseCustomAmountChange}
            onCustomAmountChange={customAmountHook.handleCustomAmountChange}
            disabled={isProcessing}
            showDefaultAmount={!!selectedMembershipCategory}
          />

          <div style={{ marginBottom: '16px' }}>
            <Checkbox 
              checked={updateValidTo}
              onChange={handleUpdateValidToChange}
              disabled={isProcessing}
            >
              Update Member's ValidTo Date
            </Checkbox>
            {updateValidTo && (
              <div style={{ marginTop: '8px' }}>
                <Checkbox 
                  checked={useCustomValidTo}
                  onChange={handleCustomValidToChange}
                  disabled={isProcessing}
                >
                  Use Custom ValidTo Date
                </Checkbox>
                {useCustomValidTo ? (
                  <div style={{ marginTop: '8px' }}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={validToDate}
                      onChange={handleValidToDateChange}
                      style={{ width: '100%' }}
                      disabled={isProcessing}
                      placeholder="Select validTo date"
                    />
                  </div>
                ) : (
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                    <strong>ValidTo Date:</strong> {selectedMonth && selectedMembershipCategory
                      ? calculateValidToDate(selectedMonth, selectedMembershipCategory, billing).format('DD/MM/YYYY')
                      : 'N/A'}
                    {selectedMonth && selectedMembershipCategory && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                        ({selectedMembershipCategory.paymentFrequency.toLowerCase() === 'monthly'
                          ? 'End of billing cycle for selected month'
                          : 'One year from selected month'})
                      </div>
                    )}
                  </div>
                )}
                {validToDate && isValidToChanged() && (
                  <Alert
                    message="ValidTo Date Change Detected"
                    description={`The selected date (${validToDate.format('DD/MM/YYYY')}) differs from the member's current validTo date (${member.validTo ? dayjs(member.validTo).format('DD/MM/YYYY') : 'Not set'}). This will update the member's validTo date when marked as paid.`}
                    type="warning"
                    showIcon
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Transaction Summary */}
          {selectedMonth && getFinalAmount() && (
            <TransactionSummary
              amount={getFinalAmount()}
              useCustomAmount={customAmountHook.useCustomAmount}
              defaultAmount={amount}
              membershipCategory={selectedMembershipCategory}
              member={member}
              showCategoryChange={true}
              showValidToChange={updateValidTo}
              validToDate={validToDate ? validToDate.toDate() : null}
              customFields={
                <>
                  <div><strong>Membership Month:</strong> {selectedMonth.format('MMMM YYYY')}</div>
                  {selectedMembershipCategory && (
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      Calculated based on {selectedMembershipCategory.paymentFrequency === 'Monthly' ? 'monthly' : 'annual'} subscription
                    </div>
                  )}
                  {!updateValidTo && (
                    <div style={{ color: '#999' }}>
                      <strong>ValidTo Date:</strong> Will not be updated
                    </div>
                  )}
                </>
              }
            />
          )}

          {/* Status Summary */}
          <StatusSummary
            items={[
              {
                label: 'Membership Category',
                isValid: !!selectedMembershipCategory,
                value: selectedMembershipCategory ? 'Selected' : 'Required'
              },
              {
                label: 'Membership Month',
                isValid: !!selectedMonth,
                value: selectedMonth ? 'Selected' : 'Required'
              },
              {
                label: 'Subscription Item',
                isValid: !!membershipSubscriptionPosItem,
                value: membershipSubscriptionPosItem ? 'Found' : 'Not Found'
              },
              {
                label: 'Amount',
                isValid: !!getFinalAmount(),
                value: getFinalAmount() ? formatCurrency(getFinalAmount() ?? 0) : 'Required',
                warning: customAmountHook.useCustomAmount ? '(Custom)' : undefined
              },
              {
                label: 'ValidTo Update',
                isValid: updateValidTo ? !!validToDate : true,
                value: updateValidTo ? (validToDate ? `Set to ${validToDate.format('DD/MM/YYYY')}` : 'Required') : 'Disabled',
                warning: updateValidTo && validToDate && isValidToChanged() ? '(Will Change)' : undefined
              },
              ...(selectedMembershipCategory && selectedMembershipCategory.name !== member.membershipCategory.name ? [{
                label: 'Category Change',
                isValid: true,
                value: `Will update from ${member.membershipCategory.name} to ${selectedMembershipCategory.name}`,
                warning: undefined
              }] : [])
            ]}
          />
        {hideButton && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #d9d9d9' }}>
            <Button onClick={onParentCancel || handleCancel} disabled={isProcessing}>
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={handleMarkAsUnpaid}
              loading={isProcessing}
              disabled={isProcessing || !selectedMonth || !selectedMembershipCategory || !getFinalAmount()}
              style={{ backgroundColor: '#ff4d4f' }}
            >
              Mark as Unpaid
            </Button>
            <Button
              type="primary"
              onClick={handleProcessTransaction}
              loading={isProcessing}
              disabled={isProcessing || !selectedMonth || !selectedMembershipCategory || !getFinalAmount()}
              style={{ backgroundColor: '#722ed1' }}
            >
              Mark as Paid
            </Button>
          </div>
        )}
    </Space>
  );

  const modalContent = (
    <Modal
      title="Create Membership Subscription Transaction"
      open={isModalVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isProcessing}>
          Cancel
        </Button>,
        <Button
          key="markAsUnpaid"
          type="primary"
          danger
          onClick={handleMarkAsUnpaid}
          loading={isProcessing}
          disabled={isProcessing || !selectedMonth || !selectedMembershipCategory || !getFinalAmount()}
          style={{ backgroundColor: '#ff4d4f' }}
        >
          Mark as Unpaid
        </Button>,
        <Button
          key="processTransaction"
          type="primary"
          onClick={handleProcessTransaction}
          loading={isProcessing}
          disabled={isProcessing || !selectedMonth || !selectedMembershipCategory || !getFinalAmount()}
          style={{ backgroundColor: '#722ed1' }}
        >
          Mark as Paid
        </Button>
      ]}
      width={700}
    >
      {formContent}
    </Modal>
  );

  if (hideButton) {
    return formContent;
  }

  return (
    <>
      <Button
        type="primary"
        icon={<CalendarOutlined />}
        onClick={showModal}
        style={{ backgroundColor: '#722ed1' }}
      >
        Create Subscription Transaction
      </Button>
      {modalContent}
    </>
  );
};

export default CreateMembershipSubscriptionTransaction;

